/**
 * PremiumUpgradePanel — shared inline upgrade CTA (G2B CP2 refactor).
 *
 * Used by:
 *   - /grants/matches (scope="matches")   — HTTP 402 match_top_page_premium_only
 *   - /grants/pipeline (scope="pipeline") — HTTP 402 pipeline_premium_only
 *
 * NEVER a modal, NEVER a redirect. The Premium upsell is a legit
 * inline surface on a legit member destination — Free users retain
 * agency (they still see the route header + can navigate back).
 *
 * Copy is scope-specific so the reassurance line references the RIGHT
 * Free-tier affordance for each surface (3 AI matches for matches
 * surface, 3 saved grants for pipeline surface).
 */
import { Link } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";

export const SCOPE_COPY = {
  matches: {
    kicker: "Premium feature",
    headline: "Your personalised Top Matches list",
    body:
      "Upgrade to Premium for unlimited AI grant matches and your personalised Top Matches list. Premium reveals every eligible grant ranked by AI match score and updates automatically as your grant profile evolves.",
    reassurance:
      "Free-tier accounts can still compute up to 3 AI matches per rolling 30 days from any grant's detail page — the Top Matches summary is the only Premium-gated surface here.",
  },
  pipeline: {
    kicker: "Premium feature",
    headline: "Your grant applications pipeline",
    body:
      "Upgrade to Premium to organise your grant applications with the pipeline board. Track every grant from Interested through Awarded, drag between stages, and keep notes on each application without leaving the board.",
    reassurance:
      "Free-tier accounts can still save up to 3 grants and update pipeline status from each grant's detail page — the kanban board is the only Premium-gated surface here.",
  },
  documents: {
    kicker: "Premium feature",
    headline: "Room for your full document library",
    body:
      "Upgrade to Premium to store up to 100 documents (1 GB total) across Business Foundation, Financial, Grant, and General categories. Premium adds bulk actions and higher per-file headroom for larger PDFs and spreadsheets.",
    reassurance:
      "Free-tier accounts keep full read, upload, download, and delete access to the vault. Premium simply raises the ceiling from 10 documents / 50 MB to 100 documents / 1 GB.",
  },
  proposals: {
    kicker: "Premium feature",
    headline: "Draft grant proposals with AI-assisted wizard",
    body:
      "Upgrade to Premium to draft grant proposals using the 10-step guided wizard. Capture your business overview, funding request, problem statement, solution, community impact, budget, and timeline — then let the AI Grant Writer draft the 11 proposal sections in the next Grant Writer update.",
    reassurance:
      "Free-tier accounts keep full access to grant discovery, up to 3 saved grants, the Document Vault, and Calendar. Premium adds proposal drafting, the AI section generator, and PDF / DOCX export.",
  },
  coach: {
    kicker: "Premium feature",
    headline: "You've used your 3 free Coach messages for this proposal",
    body:
      "Upgrade to Premium for unlimited coaching on every proposal. Coach helps you tighten weak sections, ground draft claims in your grant metadata, and surface follow-up questions reviewers will look for — without predicting funding outcomes.",
    reassurance:
      "Free-tier accounts still keep the full quality-score panel, the actionability bridge, and the read-only draft preview on this proposal. Only additional Coach turns require Premium.",
  },
  quality_score: {
    kicker: "Premium feature",
    headline: "Full quality analysis is a Premium feature",
    body:
      "Upgrade to Premium to see your proposal's completeness, length, readability, alignment, and evidence scores side-by-side — plus the server-derived weak-section list with section-specific improvement guidance.",
    reassurance:
      "Free-tier accounts keep the AI Grant Coach taster on this proposal (3 messages) and can view the section outline in the read-only draft preview.",
  },
};

export default function PremiumUpgradePanel({ scope = "matches" }) {
  const copy = SCOPE_COPY[scope] || SCOPE_COPY.matches;
  const testIdPrefix = `${scope}-upgrade`;
  return (
    <section
      data-testid={`${testIdPrefix}-cta`}
      className="mt-10 rounded-3xl p-8"
      style={{
        background: "rgba(167,139,250,0.06)",
        border: "1px solid rgba(167,139,250,0.35)",
      }}
    >
      <div className="flex items-start gap-4">
        <div
          className="rounded-full p-3 flex-shrink-0"
          style={{
            background: "rgba(167,139,250,0.14)",
            border: "1px solid rgba(167,139,250,0.35)",
          }}
        >
          <Lock className="w-5 h-5 text-v2-accent" />
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.28em] text-v2-textMuted">
            {copy.kicker}
          </p>
          <h2 className="mt-2 text-[24px] font-semibold tracking-tight text-v2-text">
            {copy.headline}
          </h2>
          <p className="mt-3 text-sm text-v2-textMuted leading-relaxed max-w-2xl">
            {copy.body}
          </p>
          <Link
            to="/pricing"
            data-testid={`${testIdPrefix}-link`}
            className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-v2-background bg-v2-accent hover:bg-white transition-colors"
          >
            Upgrade to Premium <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="mt-4 text-[11px] text-v2-textMuted leading-relaxed max-w-2xl">
            {copy.reassurance}
          </p>
        </div>
      </div>
    </section>
  );
}
